import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, BookOpen, Puzzle } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { Textarea } from "@/shared/components/ui/textarea";

import type { JobReferences } from "../types/job";
import { activeMention, insertMention, matchesQuery, mentionsOf, type Mention } from "../utils/mentions";

interface MentionTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  /** Ausente enquanto carrega: a lista abre vazia e as referências ficam sem conferência. */
  references?: JobReferences;
}

interface Option extends Mention {
  label: string;
  hint?: string;
}

const MAX_OPTIONS = 8;

/**
 * A instrução de um job com referências (AB#86605, task 86871): digitar `@`
 * abre a lista de Skills e de campos do contexto do workspace, e escolher
 * insere `@skill:<nome>` ou `@workspace:<campo>`. Abaixo, as referências que o
 * texto cita — com aviso para a que não existe, que o api recusaria ao salvar.
 */
export function MentionTextarea({ id, value, onChange, placeholder, rows = 4, references }: MentionTextareaProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);

  const allOptions = useMemo<Option[]>(
    () => [
      ...(references?.skills ?? []).map((s) => ({
        kind: "skill" as const,
        key: s.name,
        label: s.title || s.name,
        hint: s.title ? s.name : undefined,
      })),
      ...(references?.workspace ?? []).map((f) => ({
        kind: "workspace" as const,
        key: f.field_name,
        label: f.title || f.field_name,
        hint: f.field_name,
      })),
    ],
    [references],
  );

  const active = activeMention(value, caret);
  const options = active
    ? allOptions.filter((o) => matchesQuery(active.query, o.kind, o.key, o.label)).slice(0, MAX_OPTIONS)
    : [];
  // Um `@` qualquer do texto ("@time") não abre lista vazia: com as referências
  // carregadas e nada correspondendo, a lista fica fechada.
  const open = active !== null && dismissed !== active.start && (options.length > 0 || !references);

  const choose = (option: Option) => {
    if (!active) return;
    const next = insertMention(value, active.start, caret, option);
    onChange(next.text);
    setCaret(next.caret);
    setHighlight(0);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || options.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((h) => (h + step + options.length) % options.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      choose(options[Math.min(highlight, options.length - 1)]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setDismissed(active!.start);
    }
  };

  const known = useMemo(() => new Map(allOptions.map((o) => [`${o.kind}:${o.key}`, o])), [allOptions]);
  const cited = mentionsOf(value);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={ref}
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setHighlight(0);
            setDismissed(null);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setDismissed(active?.start ?? null), 150)}
          aria-autocomplete="list"
          aria-expanded={open && options.length > 0}
        />
        {open && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
          >
            {options.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {references ? t("jobs.mentions.empty") : t("jobs.mentions.loading")}
              </p>
            ) : (
              options.map((option, index) => (
                <button
                  key={`${option.kind}:${option.key}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  // mousedown, não click: o click chega depois do blur, e o blur fecha a lista.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setHighlight(index)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                    index === highlight ? "bg-accent text-accent-foreground" : "hover:bg-accent",
                  )}
                >
                  {option.kind === "skill" ? (
                    <Puzzle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <BookOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">{option.label}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                    {t(option.kind === "skill" ? "jobs.mentions.skill" : "jobs.mentions.workspace")}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {cited.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {cited.map((mention) => {
            const option = known.get(`${mention.kind}:${mention.key}`);
            const missing = Boolean(references) && !option;
            return (
              <span
                key={`${mention.kind}:${mention.key}`}
                title={missing ? t("jobs.mentions.unknown") : `@${mention.kind}:${mention.key}`}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                  missing ? "border-destructive/50 text-destructive" : "border-border text-muted-foreground",
                )}
              >
                {missing ? (
                  <AlertTriangle className="h-3 w-3" />
                ) : mention.kind === "skill" ? (
                  <Puzzle className="h-3 w-3" />
                ) : (
                  <BookOpen className="h-3 w-3" />
                )}
                {option?.label ?? mention.key}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
